import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { redirectTo, setStatusCode } from "@insite/client-framework/ServerSideRendering";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import {
    getSearchDataModeActive,
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    getAlternateLanguageUrls,
    getCanonicalUrlByPath,
    getCurrentPage,
    getLocation,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import clearProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearProducts";
// import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import loadProductsPricingAndInventory from "@insite/client-framework/Store/Pages/ProductList/Handlers/LoadProductsPricingAndInventory";
import prepareProductRecs from "@insite/client-framework/Store/Pages/ProductList/Handlers/PrepareProductRecs";
import sendOdpEvents from "@insite/client-framework/Store/Pages/ProductList/Handlers/SendOdpEvents";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import { HideFooter, HideHeader, OpenGraphImage, OpenGraphTitle } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import ProductCompareFlyOut from "@insite/content-library/Components/ProductCompareFlyOut";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getPrevCurrentNextQueryString } from "@insite/mobius/utilities/linkElementsManipulation";
import displayProducts from "@tCooneyBrothers/Store/Pages/ProductList/Handlers/DisplayProducts";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ProductListPageStyles {
    mainContainer?: InjectableCss;
}

interface DataArr {
    price: number | undefined;
    quantity: number | undefined;
}

const styles: ProductListPageStyles = {
    mainContainer: {
        css: css`
            .filterInformation {
                > div[class^="filter-text-information"] {
                    display: flex !important;
                }
            }
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const {
        pages: {
            productList: {
                isLoading,
                productFilters,
                parameter,
                isSearchPage,
                filterQuery,
                productInfosByProductId,
                isCanceled,
            },
        },
    } = state;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const productsDataView = getProductsDataView(state, parameter);
    const page = getCurrentPage(state);
    return {
        isLoading,
        stockedItemsOnly: productFilters.stockedItemsOnly,
        previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts,
        query: productFilters.query,
        productsDataView,
        firstProductDetailPath: productsDataView.value
            ? productInfosByProductId[productsDataView.value[0]?.id]?.productDetailPath
            : undefined,
        filterQuery,
        isSearchPage,
        search: location.search,
        path,
        breadcrumbLinks: state.components.breadcrumbs.links,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        websiteName: state.context.website.name,
        pages: state.pages,
        location: getLocation(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        searchDataModeActive: getSearchDataModeActive(state),
        searchPath: getSettingsCollection(state).searchSettings.searchPath,
        alternateLanguageUrls: getAlternateLanguageUrls(state, page.id),
        page,
        deferredApis: state.context.deferredApiCalls,
        productInfosByProductId,
        parameter,
        unfilteredApiParameter: state.pages.productList.unfilteredApiParameter,
        canonicalUrl: getCanonicalUrlByPath(state, path),
        enableServerSideRendering: getSettingsCollection(state).websiteSettings.enableServerSideRendering,
        isCanceled,
        realTimePricingAndQty: state.pages.productList.productInfosByProductId,
        quantities: (function () {
            const tempArray = [];
            const qtyObject = state.pages.productList.productInfosByProductId;
            for (const qty in qtyObject) {
                const key = qtyObject?.[qty]?.productId;
                tempArray.push({
                    productId: key,
                    qtyOrdered: qtyObject?.[qty]?.qtyOrdered,
                });
            }
            return tempArray;
        })(),
    };
};

const mapDispatchToProps = {
    clearAllProductFilters,
    displayProducts,
    setBreadcrumbs,
    clearProducts,
    loadProductsPricingAndInventory,
    sendOdpEvents,
    prepareProductRecs,
};

export const ProductListPageDataContext = React.createContext<{ ref?: React.RefObject<HTMLSpanElement | null> }>({
    ref: undefined,
});

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps &
    HasDeferredApiContext;

type DataLayerItem = {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    currency?: string;
    discount?: number;
    index: number;
    item_brand: string | BrandModel;
    item_category: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price: number | undefined;
    quantity: number | undefined;
};

class ProductListPage extends React.Component<Props, { sentEventFor?: string }> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }
    afterFilters = React.createRef<HTMLSpanElement | null>();

    loadProducts() {
        const { displayProducts, path, search } = this.props;

        displayProducts({
            path,
            queryString: search,
            isSearch: this.isSearchPath(),
            relevancy: true,
        });
    }

    isSearchPath() {
        const { path, searchPath } = this.props;
        return !!path.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`);
    }

    componentDidUpdate(prevProps: Props): void {
        const {
            isLoading,
            filterQuery,
            location: { pathname, search },
            productListCatalogPage,
            searchDataModeActive,
            breadcrumbLinks,
            isSearchPage,
            query,
            history,
            productsDataView,
            clearAllProductFilters,
            realTimePricingAndQty,
            path,
            quantities,
        } = this.props;

        if (productListCatalogPage) {
            this.setMetadata();
            trackPageChange();
            if (
                productListCatalogPage.categoryId !== prevProps.productListCatalogPage?.categoryId ||
                !breadcrumbLinks
            ) {
                this.setProductListBreadcrumbs();
            }
        } else if (isSearchPage) {
            // For pages that do not have a productListCatalogPage, mainly the Search Page
            this.setSearchPageMetadata(translate("Search Results"));
            trackPageChange();
            if (query !== prevProps.query || !breadcrumbLinks) {
                this.setSearchBreadcrumbs();

                if (typeof window !== "undefined") {
                    const urlElement = document.URL.includes("/Search?query=");
                    const mainElement = document.getElementById("plpFilterContent");
                    if (mainElement && urlElement) {
                        mainElement.classList.add("filterInformation");
                    }
                }
                if (query !== prevProps.query) {
                    window?.localStorage?.removeItem("searchTermRedirectUrl");
                }
            }
        }

        // Make sure we are fully loaded, since a loading product list will change the filteQuery to match the current pathname/search
        // This can trigger a loop with the pathname/search updating and the filterQuery not updated till the productList is loaded
        if (!isLoading && filterQuery !== prevProps.filterQuery) {
            const currentUrl = `${pathname}${search}`;
            const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
            if (newUrl !== currentUrl) {
                history.push(newUrl);
                return;
            }
        }

        if (!isLoading && productsDataView.value && pathname === prevProps.location.pathname) {
            if (this.checkSearchTermRedirectUrl()) {
                return;
            }
            // Check that the request is not a redirection to where we are currently
            let { searchTermRedirectUrl } = productsDataView;
            if (searchTermRedirectUrl) {
                const startsWithProtocol = searchTermRedirectUrl.lastIndexOf("http", 0) === 0;
                if (!startsWithProtocol && searchTermRedirectUrl[0] !== "/") {
                    searchTermRedirectUrl = `/${searchTermRedirectUrl}`;
                }
                const locationPath = startsWithProtocol
                    ? window?.location?.toString()
                    : `${window.location.pathname}${window.location.search}`;
                if (
                    locationPath !== searchTermRedirectUrl &&
                    window?.localStorage?.getItem("searchTermRedirectUrl") !== searchTermRedirectUrl
                ) {
                    if (startsWithProtocol) {
                        window.location.replace(searchTermRedirectUrl);
                    } else {
                        history.replace(searchTermRedirectUrl);
                    }
                    return;
                }
                if (locationPath === searchTermRedirectUrl) {
                    window?.localStorage?.setItem("searchTermRedirectUrl", searchTermRedirectUrl);
                }
            }
            this.redirectToProductDetailIfExactMatch();
        }

        if (
            !isLoading &&
            (search !== prevProps.search || path !== prevProps.path) &&
            search?.indexOf("criteria=") === -1
        ) {
            if (path !== prevProps.path) {
                clearAllProductFilters({
                    removeAll: true,
                });
            }
            this.loadProducts();
        }

        if (searchDataModeActive !== prevProps.searchDataModeActive) {
            // refresh the search results when search data mode has been toggled in the cms shell
            this.loadProducts();
        }

        this.sendOdpEventsIfNeeded();

        if (prevProps.realTimePricingAndQty !== realTimePricingAndQty) {
            /* addPriceAndQuantity function would trigger even if quantity is changed on PLP page. The following
                code prevents it from triggering addPriceAndQuantity as quantity is changed on PLP page
            */

            const qty = prevProps.quantities;
            const updatedQty = quantities;
            let executeAddPriceAndQtyFunc = true;
            if (qty.length === updatedQty.length) {
                for (let i = 0; i < qty.length - 1; i++) {
                    if (qty[i].productId === updatedQty[i].productId) {
                        if (qty[i].qtyOrdered !== updatedQty[i].qtyOrdered) {
                            executeAddPriceAndQtyFunc = false;
                            break;
                        }
                    } else {
                        return this.addPriceAndQuantity();
                    }
                }
                if (executeAddPriceAndQtyFunc) {
                    this.addPriceAndQuantity();
                }
            } else {
                this.addPriceAndQuantity();
            }
        }
    }

    UNSAFE_componentWillMount(): void {
        const {
            isCanceled,
            deferredApis,
            isSearchPage,
            callDeferredApis,
            loadProductsPricingAndInventory,
            productInfosByProductId,
            productListCatalogPage,
        } = this.props;

        if (!IS_SERVER_SIDE) {
            // cleanup state for future redirect captures
            window?.localStorage?.removeItem("searchTermRedirectUrl");
        }

        const {
            productsDataView,
            breadcrumbLinks,
            location: { pathname, search },
        } = this.props;

        if (
            ((!productsDataView.value && !productsDataView.isLoading) || isCanceled) &&
            !(IS_SERVER_SIDE && deferredApis.catalog > 0)
        ) {
            this.loadProducts();
        } else if (!IS_SERVER_SIDE && productsDataView.value) {
            if (this.checkSearchTermRedirectUrl()) {
                return;
            }
        } else if (isSearchPage && search && search.length > 0) {
            this.redirectToProductDetailIfExactMatch();
        }

        callDeferredApis({
            pricingHandler: () => {
                loadProductsPricingAndInventory({
                    products: productsDataView.value,
                    productInfosByProductId,
                });
            },
            inventoryHandler: () => {
                loadProductsPricingAndInventory({
                    products: productsDataView.value,
                    productInfosByProductId,
                });
            },
        });

        if (productListCatalogPage) {
            this.setMetadata();
            if (
                breadcrumbLinks
                    ?.slice(1)
                    ?.map(o => o.children?.toString())
                    .join(",") !==
                    productListCatalogPage.breadCrumbs
                        ?.slice(1)
                        ?.map(o => o.text)
                        .join(",") ||
                (breadcrumbLinks?.length && breadcrumbLinks[0].children !== translate("Home_Breadcrumb"))
            ) {
                this.setProductListBreadcrumbs();
            }
        } else if (isSearchPage) {
            this.setSearchPageMetadata(translate("Search Results"));
            if (!breadcrumbLinks) {
                this.setSearchBreadcrumbs();
            }
        }

        this.sendOdpEventsIfNeeded();
    }

    componentDidMount(): void {
        const { enableServerSideRendering, prepareProductRecs, productListCatalogPage, productsDataView } = this.props;
        if (enableServerSideRendering) {
            prepareProductRecs({
                isSearch: this.isSearchPath(),
                catalogPage: productListCatalogPage,
                products: productsDataView.value,
            });
        }
    }

    componentWillUnmount(): void {
        const { productsDataView, clearProducts, setBreadcrumbs } = this.props;
        if (!productsDataView.isLoading) {
            clearProducts();
            setBreadcrumbs({ links: undefined });
        }
    }

    sendOdpEventsIfNeeded = () => {
        if (IS_SERVER_SIDE) {
            return;
        }

        const { parameter, sendOdpEvents, productListCatalogPage, productsDataView, unfilteredApiParameter } =
            this.props;

        if (!unfilteredApiParameter) {
            return;
        }

        const nextParameter = JSON.stringify(parameter);
        if (nextParameter !== this.state.sentEventFor && productsDataView?.value) {
            this.setState({
                sentEventFor: nextParameter,
            });

            let lastParameter = this.state.sentEventFor ? JSON.parse(this.state.sentEventFor) : undefined;
            sendOdpEvents({
                catalogPage: productListCatalogPage,
                lastParameter,
            });
        }
    };

    checkSearchTermRedirectUrl() {
        const { productsDataView, history } = this.props;
        if (!window || !productsDataView.value) {
            return false;
        }

        let { searchTermRedirectUrl } = productsDataView;
        if (!searchTermRedirectUrl) {
            return false;
        }

        const startsWithProtocol = searchTermRedirectUrl.lastIndexOf("http", 0) === 0;
        if (!startsWithProtocol && searchTermRedirectUrl[0] !== "/") {
            searchTermRedirectUrl = `/${searchTermRedirectUrl}`;
        }

        const locationPath = startsWithProtocol
            ? window.location.toString()
            : `${window.location.pathname}${window.location.search}`;

        if (locationPath === searchTermRedirectUrl) {
            window.localStorage.setItem("searchTermRedirectUrl", searchTermRedirectUrl);
            return false;
        }

        if (window.localStorage.getItem("searchTermRedirectUrl") === searchTermRedirectUrl) {
            return false;
        }

        if (startsWithProtocol) {
            window.location.replace(searchTermRedirectUrl);
        } else {
            history.replace(searchTermRedirectUrl);
        }

        window.localStorage.setItem("searchTermRedirectUrl", searchTermRedirectUrl);

        return true;
    }

    redirectToProductDetailIfExactMatch() {
        const { productsDataView, history } = this.props;
        if (!productsDataView.value) {
            return;
        }

        const {
            location: { search },
            firstProductDetailPath,
        } = this.props;

        const { exactMatch, value: products } = productsDataView;
        if (exactMatch && products.length) {
            let productDetailUrl = firstProductDetailPath ?? products[0].canonicalUrl;
            const parsedQuery = parseQueryString<{ criteria: string; query: string }>(search);
            const query = parsedQuery.criteria || parsedQuery.query;
            if (!query) {
                return;
            }
            if (productDetailUrl.indexOf("?") !== -1) {
                productDetailUrl += `&criteria=${encodeURIComponent(query)}`;
            } else {
                productDetailUrl += `?criteria=${encodeURIComponent(query)}`;
            }

            if (IS_SERVER_SIDE) {
                setStatusCode(302);
                redirectTo(productDetailUrl);
            } else {
                history.replace(productDetailUrl);
            }
        }
    }
    getQueryStrings() {
        const { productsDataView, filterQuery } = this.props;
        if (productsDataView.value && productsDataView.pagination) {
            const { prevPageUri, nextPageUri, currentPage } = productsDataView.pagination;
            const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
                prevPageUri,
                nextPageUri,
                currentPage,
                filterQuery ?? "",
            );
            return { prevQueryString, currentQueryString, nextQueryString };
        }

        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString("", "", 1, "");
        return { prevQueryString, currentQueryString, nextQueryString };
    }

    setMetadata() {
        const { productListCatalogPage, websiteName, location, websiteSettings, breadcrumbLinks, page } = this.props;
        if (!productListCatalogPage) {
            return;
        }

        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            primaryImagePath,
            canonicalPath,
            alternateLanguageUrls,
        } = productListCatalogPage;

        const { prevQueryString, currentQueryString, nextQueryString } = this.getQueryStrings();

        setPageMetadata({
            metaDescription,
            metaKeywords,
            openGraphImage: openGraphImage || primaryImagePath,
            openGraphTitle,
            openGraphUrl,
            title,
            currentPath: location.pathname,
            canonicalPath,
            alternateLanguageUrls: alternateLanguageUrls ?? undefined,
            websiteName,
            // currentQueryString,
            prevQueryString,
            nextQueryString,
            structuredPageData: websiteSettings.enableBreadcrumbStructuredData
                ? prepareBreadcrumbStructuredPageData(breadcrumbLinks)
                : undefined,
            jsonLdScriptId: getJsonLdScriptId(page.type),
        });
    }

    verifyEndpoint() {
        const urlArray = window.location.href.split("/");
        const endpoint = urlArray[urlArray.length - 1];
        return endpoint.toLowerCase().includes("search?");
    }

    addPriceAndQuantity() {
        const { realTimePricingAndQty } = this.props;
        const dataArray = realTimePricingAndQty;
        const arr = [];
        for (const x in dataArray) {
            if (dataArray[x]?.["pricing"] && dataArray[x]?.["inventory"]) {
                arr.push({
                    price: dataArray[x]?.["pricing"]?.["unitNetPrice"],
                    quantity: dataArray[x]?.["inventory"]?.["qtyOnHand"],
                });
            }
        }
        if (arr.length > 0) {
            const filteredArr = arr.filter(num => num !== undefined); // Filter out undefined values
            this.addToDataLayer(filteredArr as Array<DataArr>); // Assert the filtered array as number[]
        }
    }

    generateItemsList = (priceArr: Array<DataArr>): DataLayerItem[] => {
        const products = this.props.productsDataView.value;
        const itemsArr: DataLayerItem[] = [];
        if (products) {
            // const breadcrumData = this.props.breadcrumbLinks;
            // const revBreadcrumData = breadcrumData ? [...breadcrumData].reverse() : [];
            // const categoryList = revBreadcrumData
            //     ?.filter((data, i, arr) => {
            //         return i < arr.length - 1 && i !== 0 && data;
            //     })
            //     .reduce((acc, data, i) => {
            //         return { ...acc, [`item_category${i + 1}`]: data.children };
            //     }, {});
            // // console.log("categoryList", categoryList);
            const categoryArr = this.props.productListCatalogPage?.uri?.split("?")[1]?.split("/") || [];
            const categories = categoryArr?.slice(1, categoryArr.length);
            const category = categories.length === 0 ? "Search" : this.props.category?.shortDescription;
            const categoryId = this.addItemListId();
            const categoryName = this.addItemListName();

            // eslint-disable-next-line array-callback-return
            products.map((product, index) => {
                itemsArr.push({
                    item_id: product.productNumber || "",
                    item_name: product.productTitle || "",
                    index,
                    item_brand: product.brand?.name || "",
                    item_category: category || "",
                    item_list_id: categoryId,
                    item_list_name: categoryName,
                    price: priceArr[index]?.price,
                    quantity: priceArr[index]?.quantity || 1,
                });
            });
        }
        return itemsArr;
    };

    addItemListId() {
        const { category } = this.props;
        if (this.isSearchPath()) {
            return "Search";
        }
        return category?.id;
    }

    addItemListName() {
        const { category } = this.props;
        if (this.isSearchPath()) {
            return "Search";
        }
        return category?.shortDescription;
    }

    // addCategory2() {
    //     if (this.isSearchPath()) {
    //         return "Search";
    //     }
    //     return this.props.breadcrumbLinks?.name;
    // }
    // addCategory3() {
    //     if (this.verifyEndpoint()) {
    //         return "Search";
    //     }
    //     return this.props.category?.name;
    // }
    // addCategory4() {
    //     if (this.verifyEndpoint()) {
    //         return "Search";
    //     }
    //     return this.props.category?.name;
    // }

    addToDataLayer(priceArr: Array<DataArr>) {
        const items = this.generateItemsList(priceArr);
        const dataLayer = (window as any).dataLayer || [];

        if (dataLayer && items.length > 0) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "view_item_list",
                ecommerce: {
                    item_list_id: this.addItemListId(),
                    item_list_name: this.addItemListName(),
                    items,
                },
            });
        }
        // console.log("datalayer view list", dataLayer);
    }

    setSearchPageMetadata(title: string) {
        const {
            websiteName,
            location,
            websiteSettings,
            fields,
            productsDataView,
            alternateLanguageUrls,
            canonicalUrl,
            breadcrumbLinks,
            page,
        } = this.props;
        const firstProduct = productsDataView.value && productsDataView.value[0];
        const { prevQueryString, currentQueryString, nextQueryString } = this.getQueryStrings();

        setPageMetadata(
            {
                title,
                currentPath: location.pathname,
                websiteName,
                openGraphImage:
                    fields.openGraphImage ||
                    firstProduct?.largeImagePath ||
                    firstProduct?.mediumImagePath ||
                    firstProduct?.smallImagePath,
                openGraphTitle: fields.openGraphTitle,
                openGraphUrl: location.pathname + location.search,
                alternateLanguageUrls,
                currentQueryString,
                prevQueryString,
                nextQueryString,
                structuredPageData: websiteSettings.enableBreadcrumbStructuredData
                    ? prepareBreadcrumbStructuredPageData(breadcrumbLinks)
                    : undefined,
                jsonLdScriptId: getJsonLdScriptId(page.type),
                canonicalPath: canonicalUrl,
            },
            websiteSettings,
        );
    }

    setProductListBreadcrumbs() {
        const { setBreadcrumbs, productListCatalogPage } = this.props;
        setBreadcrumbs({
            links: productListCatalogPage!.breadCrumbs!.map(o => ({ children: o.text, href: o.url })),
        });
    }

    setSearchBreadcrumbs() {
        const { setBreadcrumbs, query } = this.props;
        setBreadcrumbs({
            links: [
                { children: translate("Home_Breadcrumb"), href: "/" },
                { children: `${translate("Search Results")}: ${query}` },
            ],
        });
    }

    render() {
        const { productsDataView, category, id } = this.props;
        const windowIfDefined = typeof window === "undefined" ? null : window;
        if (
            productsDataView.value?.length &&
            (productsDataView.exactMatch ||
                (!IS_SERVER_SIDE &&
                    productsDataView.searchTermRedirectUrl &&
                    !windowIfDefined?.localStorage?.getItem("searchTermRedirectUrl")))
        ) {
            // prevent a flash of data when PLP immediately redirects to PDP
            return null;
        }

        return (
            <Page {...styles.mainContainer}>
                <CategoryContext.Provider value={category}>
                    <ProductListPageDataContext.Provider value={{ ref: this.afterFilters }}>
                        <Zone contentId={id} zoneName="Content" />
                        <Modals />
                        <ProductCompareFlyOut />
                    </ProductListPageDataContext.Provider>
                </CategoryContext.Provider>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(withHistory(ProductListPage))),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: false,
        supportsCategorySelection: true,
        pageType: "System",
        fieldDefinitions: [HideHeader, HideFooter, OpenGraphTitle, OpenGraphImage],
    },
};

export default pageModule;

export const ProductListPageContext = "ProductListPage";
